import { DataLoader, PageRequest } from '@aiz-gl/omni-ui';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, Observable, of, throwError } from 'rxjs';
import {
  OrderDetail,
  OrderDevicesRequest,
  OrderDevicesResponse,
  OrderHistoryDetailItem,
  OrderHistoryDetailItemResponse,
  OrderDetailResponse,
  OrderHistorySummary,
  OrderStatusCount,
  OrderStatusUpdateResponse,
  OrderSearchParams,
  PaginatedOrderHistorySummaryResponse,
} from '../../models/order.models';
import { DeviceCareCenterService } from '../device-care-center/device-care-center.service';
import { WarehouseService } from '../warehouse/warehouse.service';
import {
  getOrderNumberFromResponse,
  mapAddressResponseToModel,
  mapOrderHistorySummaryResponseToModel,
  mapOrderItemResponseToModel,
  mapOrderPageRequestToParams,
} from './order.mapper';

function getDate(value: string): Date | undefined {
  return value ? new Date(value) : undefined;
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  http = inject(HttpClient);
  dccService = inject(DeviceCareCenterService);
  warehouseService = inject(WarehouseService);

  getOrderById(id: number): Observable<OrderDetail> {
    return this.http
      .get<OrderDetailResponse>(`Order/${id}`)
      .pipe(map((response) => this.getOrderDetailFromResponse(response)));
  }

  getOrderHistoryDetails(payload: OrderDevicesRequest): Observable<OrderHistoryDetailItem[]> {
    return this.http
      .post<OrderDevicesResponse | OrderHistoryDetailItemResponse[]>(
        'orderhistory/orderdevices',
        payload,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const payload = error.error;
          const message =
            (payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
              ? payload.message
              : '') ||
            (typeof payload === 'string' ? payload : '');
          const isDetailsNotFoundMessage = /order\s*history\s*details\s*not\s*found/i.test(message);

          if (error.status === 404 || isDetailsNotFoundMessage) {
            return of([] as OrderHistoryDetailItemResponse[]);
          }

          return throwError(() => error);
        }),
        map((response) => {
          const rows = Array.isArray(response)
            ? response
            : Array.isArray(response?.items)
              ? response.items
              : Array.isArray(response?.data)
                ? response.data
              : [];

          return rows.map((item): OrderHistoryDetailItem => ({
            sku: item.sku,
            skuDescription: item.sku_description,
            quantity: item.quantity,
            unitSellingPrice: item.unit_selling_price,
            manufacturer: item.manufacturer,
            modelName: item.model_name || item.modelname || '',
            modelNumber: item.model_number || item.modelnumber || '',
            capacity: item.capacity,
            carrier: item.carrier,
            grade: item.grade,
            serialNumber: item.serial_number,
            shippingNumber: item.shipping_number,
            trackingNumber: item.tracking_number,
            lpnNumber: item.lpn_number,
            parentLpnNumber: item.parent_lpn_number,
            arInvoiceNumber: item.ar_invoice_number,
          }));
        }),
      );
  }

  private getOrderDetailFromResponse(
    response: OrderDetailResponse,
  ): OrderDetail {
    return {
      items: response.order_items.map((response) =>
        mapOrderItemResponseToModel(response),
      ),
      shipmentId: response.shipment_id,
      purchaseOrderNumber: response.po_number,
      status: response.status,

      warehouseCode: response.warehouse_code,
      warehouse: this.warehouseService.findWarehouse(response.warehouse_code),

      dccCode: response.dcc_code,
      dcc: this.dccService.getDeviceCareCenter(response.dcc_code),

      orderNumber: getOrderNumberFromResponse(response),

      buyerOrderNumber: response.web_order_id,
      buyerName: response.buyer_name,
      buyerCompanyName: response.buyer_company_name,
      buyerEmail: response.buyer_email,
      buyerCompanyNumber: response.buyer_company_number,
      salesType: response.sales_type,
      orderRouted: response.order_routed,
      erpOrderNumber: response.erp_order_number,
      syncDate: getDate(response.sync_date),
      createdOn: new Date(response.created_on),
      updatedOn: new Date(response.updated_on),
      orderId: response.atb_id,

      currencyCode: response.pay_currency_code || response.currency_code,
      orderDate: new Date(response.order_date),

      billingAddress: mapAddressResponseToModel(response.order_bill_info),
      shippingAddress: mapAddressResponseToModel(response.order_ship_info),

      shippingMethod: response.order_ship_info.ship_method,
      shippingCustomerNumber: response.order_ship_info.ship_customer_account,
      shippingCarrier: response.order_ship_info.ship_carrier,

      bulkShippingMethod: response.order_ship_info.bulk_ship_method,
      bulkShippingCustomerNumber:
        response.order_ship_info.bulk_ship_customer_account,
      bulkShippingCarrier: response.order_ship_info.bulk_ship_carrier,

      itemCost: response.order_raw_total,
      shippingCost: response.order_ship_info.ship_amount,
      taxCost: response.order_bill_info.tax_amount,
      totalCost: response.order_total,
      totalQty: response.total_qty,
      isPreOrder: response.is_pre_order,
      preOrderDate: response.pre_order_date,
      notes: response.order_notes,

      promptForSync:
        !response.is_pre_order &&
        !response.erp_order_number &&
        !response.sync_date,
    };
  }

  orderDataLoader: DataLoader<OrderHistorySummary> = (request: PageRequest) => {
    // Region ID is a required filter for order history summary.
    if (!request.filter || !request.filter['regionId'])
      return Promise.resolve({
        data: [],
        totalRecords: 0,
        pageNumber: 1,
        pageSize: 0,
      });
    return firstValueFrom(
      this.http
        .post<PaginatedOrderHistorySummaryResponse>(
          `Order/search`,
          mapOrderPageRequestToParams(request),
        )
        .pipe(
          map((response) => {
            return {
              data: response.items.map((response) =>
                mapOrderHistorySummaryResponseToModel(response),
              ),
              totalRecords: response.total_count,
              pageNumber: response.page_number,
              pageSize: response.page_size,
            };
          }),
        ),
    );
  };

  searchOrdersForRma(
    request: PageRequest,
    warehouseChannelCombinations: Array<{ warehouseCode: string; channelCode: string }>,
  ) {
    // Region ID is a required filter for order history summary.
    if (!request.filter || !request.filter['regionId']) {
      return Promise.resolve({
        data: [],
        totalRecords: 0,
        pageNumber: 1,
        pageSize: 0,
      });
    }

    const payload = {
      ...mapOrderPageRequestToParams(request),
      warehouseChannelCombinations,
    };

    return firstValueFrom(
      this.http
        .post<PaginatedOrderHistorySummaryResponse>('Order/search', payload)
        .pipe(
          map((response) => {
            return {
              data: response.items.map((item) =>
                mapOrderHistorySummaryResponseToModel(item),
              ),
              totalRecords: response.total_count,
              pageNumber: response.page_number,
              pageSize: response.page_size,
            };
          }),
        ),
    );
  }

  downloadOrders(request: PageRequest) {
    return this.http.post(
      'Order/Download',
      mapOrderPageRequestToParams(request),
      {
        observe: 'response',
        responseType: 'blob',
      },
    ) as Observable<HttpResponse<Blob>>;
  }

  downloadErpLogs(orderId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`Order/download-erp-logs/${orderId}`, {
      observe: 'response',
      responseType: 'blob',
    }) as Observable<HttpResponse<Blob>>;
  }

  syncOrderDate(orderNumber: number): Observable<string> {
    const params = new HttpParams().set('atbId', orderNumber);
    return this.http.post('Order/SyncOrder', null, {
      params,
      responseType: 'text',
    });
  }

  updateOrderStatus(orderId: number, status: string) {
    const body = { status };
    return this.http
      .put<OrderStatusUpdateResponse>(`order/${orderId}`, body)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  getStatusCounts(
    payload: Pick<
      OrderSearchParams,
      | 'regionId'
      | 'startDate'
      | 'endDate'
      | 'buyerCompanyName'
      | 'orderNo'
      | 'dcc'
      | 'warehouse'
      | 'erp'
      | 'status'
      | 'salesChannelCode'
      | 'erpOrderNumber'
    >,
  ): Promise<OrderStatusCount[]> {
    return firstValueFrom(
      this.http.post<OrderStatusCount[]>('Order/status-counts', payload),
    );
  }
}
